/* ============================================================================
   ONE UNIFIED ACTIVITY TIMELINE                    (SITE-RULES 11b)

   One chronological history per person, merged from everything the product already
   knows: the enquiry carried forward, trials, conversion, attendance, notes, InBody
   scans, interactions, risk signals, follow-ups and their outcomes. Derived where the
   data already exists and stored only where it cannot be derived — a phone call, a
   message sent, an outcome recorded.

   Every event carries its SOURCE and its OWNER, so the record answers "where did this
   come from and who handled it", not just "what happened".

   THERE IS NO SEPARATE INTERACTION HISTORY TAB (the owner ruled this explicitly).
   Interactions are events in the one timeline.

   NEWEST FIRST, always. The first view opens on 8 to 10 meaningful events with an
   expand for the rest: a four-year member has forty-plus notes, and the first view is a
   history, not a data dump. Pass `limit`.
   ============================================================================ */
import { isLead } from './lifecycle.js';
export const EVENT_KINDS = {
    lead_created: { label: 'New lead', icon: 'leads', tone: 'info' },
    contacted: { label: 'Contacted', icon: 'campaigns', tone: 'info' },
    trial_booked: { label: 'Trial booked', icon: 'tasks', tone: 'info' },
    trial_attended: { label: 'Trial attended', icon: 'members', tone: 'good' },
    converted: { label: 'Became a member', icon: 'members', tone: 'good' },
    attendance: { label: 'Class attendance', icon: 'tasks', tone: 'good' },
    missed: { label: 'Missed class', icon: 'tasks', tone: 'warn' },
    note: { label: 'Note', icon: 'segments', tone: 'plain' },
    call: { label: 'Phone call', icon: 'campaigns', tone: 'plain' },
    sms: { label: 'Message', icon: 'campaigns', tone: 'plain' },
    email: { label: 'Email', icon: 'campaigns', tone: 'plain' },
    inbody: { label: 'InBody scan', icon: 'dashboard', tone: 'info' },
    membership: { label: 'Membership', icon: 'admin', tone: 'info' },
    cancelled: { label: 'Membership ended', icon: 'admin', tone: 'risk' },
    signal: { label: 'Risk signal', icon: 'admin', tone: 'risk' },
    followup: { label: 'Follow-up', icon: 'tasks', tone: 'info' },
    outcome: { label: 'Outcome', icon: 'tasks', tone: 'good' },
    care: { label: 'Care Track', icon: 'admin', tone: 'plain' },
    macros: { label: 'Macro targets', icon: 'dashboard', tone: 'info' },
};
/* ── Source vocabulary ────────────────────────────────────────────────────
   DISPLAY ONLY. The stored value is never rewritten: whatever the migration carried in,
   or whatever Mindbody last sent, is the record of truth.

   Placeholders mean ONE thing — nobody recorded it. The migration spells that at least
   six ways ("None" on all 126 LAB leads, "Other"/"Others", blank, "Unknown"), and each
   spelling used to surface as its own fake category. They collapse to one honest label
   instead, and it is never "Other". */
export const SOURCE_UNRECORDED = 'Not recorded';
const SOURCE_PLACEHOLDERS = new Set([
    '', 'none', 'other', 'others', 'unknown', 'unspecified', 'n/a', 'na', 'null',
    'undefined', '-', '—', '.',
]);
/**
 * `value` is the spelling ALREADY IN THE DATA so manual entry does not fork the
 * taxonomy ("WalkIn" is what Mindbody writes; adding "Walk-in" would create a second
 * bucket meaning the same thing). `label` is what a human should see.
 */
export const LEAD_SOURCES = [
    { value: 'WalkIn', label: 'Walk-in' },
    { value: 'Phone', label: 'Phone' },
    { value: 'Website', label: 'Website' },
    { value: 'Email', label: 'Email' },
    { value: 'MBO App', label: 'Mindbody app' },
    { value: 'Referral', label: 'Member referral' },
    { value: 'Instagram', label: 'Instagram' },
    { value: 'Facebook', label: 'Facebook' },
    { value: 'Google Search', label: 'Google search' },
    { value: 'YELP', label: 'Yelp' },
    { value: 'Event', label: 'Event / community' },
];
export function isUnrecordedSource(v) {
    return SOURCE_PLACEHOLDERS.has(String(v ?? '').trim().toLowerCase());
}
/**
 * Known values get their friendly spelling ("WalkIn" → "Walk-in"); anything the
 * migration carried that we have no label for is shown VERBATIM rather than guessed at,
 * so an unfamiliar source is never silently renamed.
 */
export function sourceLabel(v) {
    if (isUnrecordedSource(v))
        return SOURCE_UNRECORDED;
    const raw = String(v).trim();
    const hit = LEAD_SOURCES.find(s => s.value.toLowerCase() === raw.toLowerCase());
    return hit ? hit.label : raw;
}
export function timeline(rec, opts = {}) {
    if (!rec)
        return [];
    const ev = [];
    const push = (at, kind, title, detail, who, source) => {
        if (!at)
            return;
        const meta = EVENT_KINDS[kind];
        ev.push({
            at,
            kind,
            title: title || meta?.label || kind,
            detail: detail || '',
            who: who || null,
            source: source || 'System',
            tone: meta?.tone ?? 'plain',
            icon: meta?.icon ?? 'tasks',
        });
    };
    const noteWho = (n) => n.who ?? n.by ?? null;
    const lead = isLead(rec);
    if (lead) {
        const l = rec;
        push(l.createdAt, 'lead_created', 'New lead', 'Source: ' + sourceLabel(l.source), null, 'System');
        for (const n of l.notes ?? [])
            push(n.at, 'note', 'Note', n.text, noteWho(n), 'Staff');
        if (l.stage === 'won') {
            push(l.lastActivity, 'converted', 'Became a member', 'Converted from ' + (l.interest || 'trial'), l.owner, 'System');
        }
    }
    else {
        const m = rec;
        /* THE ENQUIRY IS NOT A SEPARATE RECORD (SITE-RULES 11c). Everything the lead did
           before they joined is THIS person's history, so it unfolds here: the original
           enquiry, every trial, and every note from before conversion. The member record is
           where a coach looks; it must not begin mid-story. */
        const fl = m.fromLead;
        if (fl) {
            push(fl.createdAt, 'lead_created', 'New lead', 'Source: ' + sourceLabel(fl.source) + (fl.interest ? ' · interested in ' + fl.interest : ''), fl.owner, 'System');
            for (const n of fl.notes ?? []) {
                push(n.at, 'note', 'Note', n.text, n.who ?? n.by ?? null, 'Staff');
            }
            for (const t of fl.trials ?? []) {
                push(t.at, 'trial_attended', t.name + ' attended', t.coach ? 'Run by ' + t.coach : '', t.coach, 'Attendance');
            }
        }
        push(m.joinedAt, 'converted', 'Became a member', (fl ? 'Converted from lead · ' : '') + 'Source: ' + sourceLabel(m.source)
            + (m.plan ? ' · ' + m.plan : ''), m.supportCoach, 'System');
        for (const v of m.visits ?? []) {
            push(v.date, 'attendance', 'Class attendance', (v.className || 'Session') + (v.coach ? ' · ' + v.coach : ''), v.coach, 'Attendance');
        }
        for (const s of m.scans ?? []) {
            push(s.date, 'inbody', 'InBody scan', [s.weight ? 'Weight: ' + s.weight + ' lbs' : null, s.pbf ? 'Body fat: ' + s.pbf + '%' : null]
                .filter(Boolean).join(' · '), null, 'InBody');
        }
        for (const n of m.notes ?? [])
            push(n.at, 'note', 'Note', n.text, noteWho(n), 'Staff');
        if (m.cancelledAt)
            push(m.cancelledAt, 'cancelled', 'Membership ended', m.plan || '', null, 'System');
        if (m.suppression) {
            push(m.suppression.declaredAt, 'care', m.suppression.kind === 'hold' ? 'Placed on hold' : 'Placed on Care Track', m.suppression.reason, m.suppression.declaredBy, 'Staff');
        }
    }
    /* Stored interactions and signals — the things that cannot be derived. */
    const person = rec;
    for (const i of person.interactions ?? []) {
        push(i.at, i.kind ?? 'call', null, i.text ?? '', i.who ?? i.by ?? null, i.source ?? 'Staff');
    }
    for (const s of person.signals ?? []) {
        push(s.at, 'signal', s.title ?? 'Risk signal', s.detail ?? '', null, 'System');
    }
    /* AN OUTCOME REACHES US TWICE: the follow-up carries `outcomeAt`, and the action that
       recorded it also called recordEvent() and left an 'outcome' interaction on the same
       day. They are ONE event, so the stored follow-up yields to the interaction, which is
       the richer record — it names who did it and where from. */
    const loggedOutcome = new Set((person.interactions ?? []).filter(i => i.kind === 'outcome').map(i => i.at));
    for (const f of person.followUps ?? []) {
        /* `at` is when the follow-up was RAISED. types.ts's FollowUp carries only `due`, so
           that stands in when nothing better is held. See the report. */
        push(f.at ?? f.due, 'followup', f.action ?? 'Follow-up', f.why ?? '', f.owner, 'Follow-up');
        if (f.outcomeAt && !loggedOutcome.has(f.outcomeAt)) {
            push(f.outcomeAt, 'outcome', f.outcome ?? 'Outcome', f.outcomeDetail ?? '', f.owner, 'Follow-up');
        }
    }
    /* NEWEST FIRST. Compared as strings: 'YYYY-MM-DD' and a full ISO instant sort
       correctly against each other, and a date-only event on the same day reads as the
       older of the two, which is the honest answer when no time was recorded. */
    let out = ev
        .filter(e => e.at)
        .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
    if (opts.kinds && opts.kinds.length) {
        const want = new Set(opts.kinds);
        out = out.filter(e => want.has(e.kind));
    }
    if (opts.limit)
        out = out.slice(0, opts.limit);
    return out;
}
/**
 * The shape of what `recordEvent()` appends. The persistence half of util.js's
 * `recordEvent` — the provider `_save()` call — does NOT belong in `shared/`: the API
 * writes a row into `events`, the browser's SeedSource writes it into the in-memory db.
 * This builds the event; the DataSource stores it.
 *
 * Every action taken anywhere in the product goes through this, so the timeline is the
 * single record of what was done (SITE-RULES 11b).
 */
export function recordEvent(at, kind, text, who, source) {
    return { at, kind, text, who: who ?? null, source: source || 'Staff' };
}
